import {
  Component,
  TemplateRef,
  QueryList,
  ElementRef,
  ChangeDetectionStrategy,
  ViewChildren,
  AfterViewInit,
  OnDestroy,
  DestroyRef,
  inject,
  signal,
  effect,
  Input
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { ActiveDescendantKeyManager, Highlightable } from '@angular/cdk/a11y';
import {
  BsItemContext,
  BsEmptyContext,
  BsFooterContext
} from './bs-autocomplete.directives';

@Component({
  selector: 'bs-autocomplete-option',
  standalone: true,
  template: `<ng-content></ng-content>`,
  host: {
    'class': 'dropdown-item bs-ac-item',
    '[class.active]': 'active',
    '[class.disabled]': 'disabled',
    '[attr.id]': 'id',
    'role': 'option',
    '[attr.aria-selected]': 'active',
    '[attr.aria-disabled]': 'disabled'
  }
})
export class BsAutocompleteOptionComponent<T> implements Highlightable {
  @Input() id!: string;
  @Input() value!: T;
  @Input() disabled = false;

  active = false;

  setActiveStyles(): void {
    this.active = true;
  }

  setInactiveStyles(): void {
    this.active = false;
  }

  getLabel(): string {
    return '';
  }
}

export type FooterMode = 'always' | 'items-only' | 'empty-only';

@Component({
  selector: 'bs-autocomplete-panel',
  standalone: true,
  imports: [NgTemplateOutlet, BsAutocompleteOptionComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="dropdown-menu show bs-ac-panel" role="presentation" [style.max-height]="maxHeight()">
      @if (loading()) {
        <!-- Loading region -->
        <div role="status" class="px-3 py-2 text-body-secondary">
          <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
          Loading...
        </div>
      } @else if (items().length > 0) {
        <!-- Listbox when items exist -->
        <div
          [id]="listboxId()"
          role="listbox"
          class="bs-ac-listbox"
        >
          @for (item of items(); track $index; let i = $index) {
            <bs-autocomplete-option
              [id]="listboxId() + '-option-' + i"
              [value]="item"
              (click)="onOptionClick(item); $event.stopPropagation()"
              (mouseenter)="onOptionMouseEnter(i)"
            >
              @if (itemTemplate()) {
                <ng-container
                  [ngTemplateOutlet]="itemTemplate()"
                  [ngTemplateOutletContext]="getItemContext(item, i)"
                ></ng-container>
              } @else {
                {{ displayWith()(item) }}
              }
            </bs-autocomplete-option>
          }
        </div>
      } @else {
        <!-- Empty region -->
        <div class="bs-ac-empty" role="note">
          @if (emptyTemplate()) {
            <ng-container
              [ngTemplateOutlet]="emptyTemplate()"
              [ngTemplateOutletContext]="getEmptyContext()"
            ></ng-container>
          } @else {
            <div class="px-3 py-2 text-body-secondary">{{ emptyText() }}</div>
          }
        </div>
      }

      @if (footerTemplate() && shouldShowFooter()) {
        <!-- Footer region -->
        <div
          class="bs-ac-footer"
          role="presentation"
        >
          <ng-container
            [ngTemplateOutlet]="footerTemplate()"
            [ngTemplateOutletContext]="getFooterContext()"
          ></ng-container>
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }

    .bs-ac-panel {
      position: static;
      display: block;
      max-height: var(--bs-ac-max-height, 300px);
      overflow: auto;
      padding: 0;
      width: 100%;
    }

    .bs-ac-listbox {
      padding: 0.25rem 0;
    }

    .bs-ac-item {
      cursor: pointer;
      padding: 0.5rem 1rem;
    }

    .bs-ac-item:not(.disabled):hover,
    .bs-ac-item.active {
      background-color: var(--bs-tertiary-bg);
    }

    .bs-ac-empty {
      padding: 0.25rem 0;
    }

    .bs-ac-footer {
      border-top: 1px solid var(--bs-border-color);
    }
  `]
})
export class BsAutocompletePanelComponent<T> implements AfterViewInit, OnDestroy {
  readonly elementRef = inject(ElementRef<HTMLElement>);
  private destroyRef = inject(DestroyRef);

  // Writable signals for properties set by parent
  readonly items = signal<T[]>([]);
  readonly loading = signal(false);
  readonly query = signal('');
  readonly listboxId = signal('');
  readonly maxHeight = signal('300px');
  readonly emptyText = signal('No results');
  readonly footerMode = signal<FooterMode>('always');
  readonly displayWith = signal<(value: T | null) => string>((v) => String(v ?? ''));

  readonly itemTemplate = signal<TemplateRef<BsItemContext<T>> | null>(null);
  readonly emptyTemplate = signal<TemplateRef<BsEmptyContext> | null>(null);
  readonly footerTemplate = signal<TemplateRef<BsFooterContext> | null>(null);

  onSelect!: (item: T) => void;

  @ViewChildren(BsAutocompleteOptionComponent)
  options!: QueryList<BsAutocompleteOptionComponent<T>>;

  keyManager!: ActiveDescendantKeyManager<BsAutocompleteOptionComponent<T>>;

  private pendingTimeout: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  constructor() {
    // Effect to reinitialize key manager when items change
    effect(() => {
      const _ = this.items();
      if (this.keyManager) {
        if (this.pendingTimeout) clearTimeout(this.pendingTimeout);
        this.pendingTimeout = setTimeout(() => {
          if (!this.destroyed) this.initKeyManager();
        });
      }
    });
  }

  ngAfterViewInit(): void {
    this.initKeyManager();
    this.options.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.initKeyManager());
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.pendingTimeout) clearTimeout(this.pendingTimeout);
  }

  private initKeyManager(): void {
    this.keyManager = new ActiveDescendantKeyManager(this.options)
      .withWrap()
      .withTypeAhead();
  }

  getItemContext(item: T, index: number): BsItemContext<T> {
    const option = this.options?.get(index);
    return {
      $implicit: item,
      query: this.query(),
      index,
      active: option?.active ?? false
    };
  }

  getEmptyContext(): BsEmptyContext {
    return { query: this.query() };
  }

  getFooterContext(): BsFooterContext {
    return {
      query: this.query(),
      itemsCount: this.items().length,
      loading: this.loading()
    };
  }

  shouldShowFooter(): boolean {
    switch (this.footerMode()) {
      case 'items-only':
        return this.items().length > 0;
      case 'empty-only':
        return this.items().length === 0;
      case 'always':
      default:
        return true;
    }
  }

  onOptionClick(item: T): void {
    this.onSelect?.(item);
  }

  onOptionMouseEnter(index: number): void {
    this.keyManager?.setActiveItem(index);
  }

  scrollActiveOptionIntoView(): void {
    const activeOption = this.keyManager?.activeItem;
    if (activeOption) {
      const element = this.options
        .toArray()
        .find((opt) => opt === activeOption);
      if (element) {
        const optionElement = document.getElementById(element.id);
        optionElement?.scrollIntoView({ block: 'nearest' });
      }
    }
  }
}
